import { useEffect, useState } from 'react';
import TileCard from '../TileCard';
import { getCachedResponse, getE2eResults } from '../../api/client';
import type { Env, E2eResults, E2eResultRow } from '../../types';

const HELP =
  'What the end-to-end suite covers. The catalogue below is generated from the ' +
  'latest run itself — every check the harness executed — so it stays current as ' +
  'tests are added. No part of this is hand-maintained.';

// Human-readable phase headings + descriptions. Unknown phases fall back to the
// raw phase name with a generic blurb, so adding a new phase never breaks this.
const PHASE_INFO: Record<string, { title: string; blurb: string }> = {
  validation: {
    title: 'Validation suite',
    blurb:
      'Each case sends a request that is valid except for the one rule under test, ' +
      'so exactly that rule fires. The harness asserts the gateway returns HTTP 400 ' +
      'with the documented error message, before any Logic App or Salesforce work — ' +
      'so these never create data. Grouped by operation below.',
  },
  lifecycle: {
    title: 'Lifecycle',
    blurb:
      'A single contact and case are driven through their full life on the live ' +
      'gateway, with unique self-cleaning test data. Asynchronous steps (202) are ' +
      'polled via a find until the expected state is reached. Steps run in order:',
  },
};

function titleCase(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// Preserve first-seen order of phases / ops as they appear in the run.
function orderedKeys<T>(rows: T[], key: (r: T) => string): string[] {
  const seen: string[] = [];
  for (const r of rows) {
    const k = key(r);
    if (!seen.includes(k)) seen.push(k);
  }
  return seen;
}

export default function E2eAboutTile({ env, refreshToken = 0 }: { env: Env; refreshToken?: number }) {
  const [data, setData] = useState<E2eResults | null>(() =>
    getCachedResponse<E2eResults>('e2e-results', { env }),
  );
  const [loading, setLoading] = useState(data === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const cached = getCachedResponse<E2eResults>('e2e-results', { env });
    if (cached) setData(cached);
    setLoading(cached === null);
    setError(null);
    getE2eResults(env)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [env, refreshToken]);

  const rows = data?.rows ?? [];
  const phases = orderedKeys(rows, (r) => r.phase);

  return (
    <TileCard title="What the E2E suite tests" help={HELP} loading={loading} error={error}>
      <div className="e2e-about">
        <p>
          The end-to-end suite drives the live APIM gateway exactly as a vendor would and
          records every call. It has two phases: a <strong>validation suite</strong> that
          proves every documented input rule is enforced at the gateway, and a{' '}
          <strong>lifecycle</strong> that proves a contact and a case can be created, found,
          updated, de-duplicated and deleted end to end. A run fails if any single check does
          not match its expected outcome.
        </p>

        {data?.status === 'not-configured' && (
          <p className="tile-empty">
            The live catalogue of individual checks appears here once the first run has been
            published for {env.toUpperCase()}.
          </p>
        )}
        {data?.status === 'error' && <div className="tile-error">Query failed: {data.message}</div>}

        {data?.status === 'ok' &&
          phases.map((phase) => {
            const info = PHASE_INFO[phase] ?? {
              title: titleCase(phase),
              blurb: 'Checks executed in this phase:',
            };
            const phaseRows = rows.filter((r) => r.phase === phase);
            const ops = orderedKeys(phaseRows, (r) => r.op);
            const isValidation = phase === 'validation';
            return (
              <section key={phase} className="e2e-about-phase">
                <h3>
                  {info.title} <span className="muted">({phaseRows.length} checks)</span>
                </h3>
                <p>{info.blurb}</p>
                {isValidation ? (
                  ops.map((op) => {
                    const opRows = phaseRows.filter((r) => r.op === op);
                    return (
                      <div key={op} className="e2e-about-op">
                        <h4>
                          {op} <span className="muted">({opRows.length})</span>
                        </h4>
                        <ul>
                          {opRows.map((r: E2eResultRow) => (
                            <li key={r.name}>
                              <code>{r.name}</code>
                              {r.expected ? <span className="muted"> — {r.expected}</span> : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })
                ) : (
                  <ol className="e2e-about-steps">
                    {phaseRows.map((r: E2eResultRow) => (
                      <li key={r.name}>
                        <code>{r.name}</code> <span className="muted">({r.op})</span>
                        {r.expected ? <span className="muted"> — {r.expected}</span> : null}
                      </li>
                    ))}
                  </ol>
                )}
              </section>
            );
          })}
      </div>
    </TileCard>
  );
}
